'use client';

import { ReactComponent as GiftCard } from '@assets/icons/gift-card.svg';
import { BANK_ICONS } from 'constant';
import Image from 'next/image';

import { Box, Heading, Spinner, useTheme, VStack } from '@chakra-ui/react';

import { getCardType } from 'utils';

import { CardType, DepositOptionsInterface, GiftCardInterface } from 'types';

import { Text } from 'ui/Text';

type LoaderProps = {
  depositOptions: DepositOptionsInterface | null;
  bg?: string;
  cardNumber: string;
  cardType?: CardType;
  giftCards: GiftCardInterface[];
};

const CardProcessingLoader = ({
  depositOptions,
  cardNumber,
  cardType,
  bg = 'transparent',
  giftCards = []
}: LoaderProps) => {
  const {
    colors: {
      palette: {
        components: {
          paymentList: { cardProcessingLoader: styles }
        }
      }
    }
  } = useTheme();

  const bank = cardType || getCardType(cardNumber);
  const bankIcon = bank ? BANK_ICONS[bank] : '';

  const getTitle = () => {
    if (depositOptions?.req_guarantee) {
      return `Validating Credit Card x${cardNumber.slice(-4)}`;
    }
    if (cardNumber) {
      return `Processing Credit Card x${cardNumber.slice(-4)}`;
    }
    if (giftCards.length) {
      const cardsNumber = giftCards.map(
        (c) => `x${(c?.serial ?? '').slice(-4)}`
      );

      return `Processing Gift Card${
        cardsNumber.length > 1 ? 's' : ''
      } ${cardsNumber.join(', ')}`;
    }

    return '';
  };

  const getHelperText = () => {
    if (depositOptions?.req_guarantee) {
      return 'A temporary $1 charge will be made to verify the card selected';
    }
    if (depositOptions?.req_deposit) {
      return `A deposit of $${
        depositOptions?.deposit_amount ?? 0
      } is being charged`;
    }

    return '';
  };

  const cardImage = bankIcon ? (
    <Box
      width={120}
      height='40px'
      mt='14px'
      position='relative'
      sx={{
        img: {
          width: 'auto !important'
        }
      }}
    >
      <Image
        src={bankIcon}
        alt='Credit card'
        fill
        sizes='100px'
        style={{
          margin: 'auto'
        }}
      />
    </Box>
  ) : (
    <GiftCard width={90} height={90} fill={styles.iconFill} />
  );

  return (
    <VStack
      align='center'
      justify='center'
      position='fixed'
      width='100vw'
      height='100vh'
      top='0'
      left='0'
      bg={bg}
      zIndex='9999'
      backdropFilter='blur(10px)'
    >
      <VStack
        bg={styles.boxBg}
        py={{ base: '30px', sm: '40px' }}
        px='20px'
        borderRadius='20px'
        boxShadow='lg'
        textAlign='center'
        w='full'
        maxW={{ base: '90%', sm: 'container.sm' }}
        gap={{ base: '20px', lg: '30px' }}
      >
        {cardImage}
        <Heading
          fontSize={{ base: '22px', md: '24px' }}
          fontWeight={{ base: 400, sm: 600 }}
          color={styles.heading}
          textAlign='center'
        >
          {getTitle()}
        </Heading>
        <Spinner
          width='90px'
          height='90px'
          color={styles.spinnerColor}
          mb={4}
          my='15px'
          thickness='6px'
        />
        <Text fontSize={{ base: '14px', md: '16px' }} color={styles.helperText}>
          {getHelperText()}
        </Text>
      </VStack>
    </VStack>
  );
};

export default CardProcessingLoader;
